import React from "react";

import FolderIcon from "@mui/icons-material/Folder";
import {
  Box,
  Card,
  CardContent,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import { getCardStyles, sectionLabelSx } from "src/shell/styles/themeUtils";

import type { Team } from "../../../../organization/types/OrganizationTypes";
import { focusRingSx, slugify } from "../utils";

interface BasicInfoSectionProps {
  name: string;
  setName: React.Dispatch<React.SetStateAction<string>>;
  aliasOverride: string;
  setAliasOverride: React.Dispatch<React.SetStateAction<string>>;
  aliasTouched: boolean;
  setAliasTouched: React.Dispatch<React.SetStateAction<boolean>>;
  description: string;
  setDescription: React.Dispatch<React.SetStateAction<string>>;
  nameError: string;
  validateName: (value: string) => boolean;
  isSubmitting: boolean;
  effectiveAlias: string;
  teams: Team[];
  teamId: string;
  setTeamId: React.Dispatch<React.SetStateAction<string>>;
  orgAlias?: string;
}

export function BasicInfoSection({
  name,
  setName,
  setAliasOverride,
  setAliasTouched,
  description,
  setDescription,
  nameError,
  validateName,
  isSubmitting,
  effectiveAlias,
  teams,
  teamId,
  setTeamId,
  orgAlias,
}: BasicInfoSectionProps): React.ReactElement {
  const theme = useTheme();

  return (
    <Card sx={getCardStyles(theme)}>
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
          <FolderIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" sx={sectionLabelSx}>
            Basic Information
          </Typography>
        </Stack>

        <Stack spacing={3}>
          <TextField
            label="Project Name"
            required
            fullWidth
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setAliasTouched(false);
              if (nameError) validateName(e.target.value);
            }}
            onBlur={(e) => validateName(e.target.value)}
            error={!!nameError}
            helperText={nameError}
            disabled={isSubmitting}
            autoFocus
            placeholder="e.g. Data Platform"
            sx={focusRingSx(theme)}
          />

          <Box>
            <TextField
              label="URL Slug"
              fullWidth
              value={effectiveAlias}
              onChange={(e) => {
                setAliasTouched(true);
                setAliasOverride(slugify(e.target.value));
              }}
              disabled={isSubmitting}
              helperText="Lowercase letters, numbers, and hyphens only"
              sx={focusRingSx(theme)}
              InputProps={{
                sx: { fontFamily: "monospace", fontSize: "0.875rem" },
              }}
            />
            {effectiveAlias && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.5,
                  ml: 1.5,
                  color: "text.secondary",
                  fontFamily: "monospace",
                }}
              >
                Preview: /organizations/{orgAlias}/projects/{effectiveAlias}
              </Typography>
            )}
          </Box>

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of your project"
            disabled={isSubmitting}
            sx={focusRingSx(theme)}
          />

          {/* Team selector */}
          {teams.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1 }}>
                Team
              </Typography>
              <Select
                fullWidth
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                disabled={isSubmitting}
                displayEmpty
                sx={focusRingSx(theme)}
                renderValue={(value) => {
                  if (!value || value === "") return "Select a team (optional)";
                  const team = teams.find((t) => t.id === Number(value));
                  return team
                    ? `${team.name}${team.department ? ` — ${team.department.name}` : ""}`
                    : "Unknown";
                }}
              >
                <MenuItem value="">
                  <em>No team (unassigned)</em>
                </MenuItem>
                {teams.map((team) => (
                  <MenuItem key={team.id} value={String(team.id)}>
                    <Stack>
                      <Typography variant="body2" fontWeight={500}>
                        {team.name}
                      </Typography>
                      {team.department && (
                        <Typography variant="caption" color="text.secondary">
                          {team.department.name}
                        </Typography>
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
